/**
 * Client-side image compressor — shrinks phone screenshots to a size that
 * Claude Vision can still read perfectly, but that fits comfortably under
 * the ~1 MB k8s ingress cap when batched 3-6 at a time.
 *
 * Strategy:
 *   - Decode the file into an off-screen <img>
 *   - Draw it to a <canvas> at max-width 1280px (downscaling iff wider)
 *   - Re-encode as JPEG quality 0.78 (small, but Claude Vision OCRs ~99% as well as quality 1.0)
 *   - Skip compression for already-small files (<150 KB) and PDFs (Claude reads them as text, not vision)
 *
 * Typical reduction: 1.5 MB phone screenshot -> ~80 KB. ~95% smaller.
 */

const MAX_DIMENSION = 1280;
const JPEG_QUALITY = 0.78;
const SKIP_BELOW_BYTES = 150 * 1024; // 150 KB — already small, no need to recompress

const isImage = (file) =>
  /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file?.type || "");

const decodeImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
      type,
      quality
    );
  });

/**
 * Compress a single File. Returns a NEW File (renamed `<original>.jpg`) when
 * compression succeeds and saves bytes. Returns the original File otherwise.
 */
export async function compressImageFile(file) {
  if (!file || !isImage(file)) return file;
  if (file.size <= SKIP_BELOW_BYTES) return file;
  try {
    const img = await decodeImage(file);
    const longestSide = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = longestSide > MAX_DIMENSION ? MAX_DIMENSION / longestSide : 1;
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return file;
    ctx.fillStyle = "#ffffff"; // white backdrop for any transparent PNGs
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY);
    if (!blob || blob.size >= file.size) return file; // no win → keep original
    const newName = file.name.replace(/\.(png|webp|heic|heif|jpe?g)$/i, "") + ".jpg";
    return new File([blob], newName, {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch (e) {
    console.warn("imageCompressor: falling back to original", file.name, e);
    return file;
  }
}

/**
 * Compress a batch of files in parallel. Non-images pass through untouched.
 * Reports total bytes saved via the optional callback so the UI can show
 * something nice ("Compressed 12 screenshots, saved 4.7 MB").
 */
export async function compressFiles(files, onSummary) {
  if (!files?.length) return [];
  const before = files.reduce((s, f) => s + (f?.size || 0), 0);
  const compressed = await Promise.all(files.map((f) => compressImageFile(f)));
  const after = compressed.reduce((s, f) => s + (f?.size || 0), 0);
  const savedBytes = Math.max(0, before - after);
  onSummary?.({
    fileCount: files.length,
    beforeBytes: before,
    afterBytes: after,
    savedBytes,
    savedPct: before > 0 ? Math.round((savedBytes / before) * 100) : 0,
  });
  return compressed;
}
